import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star, UserRound, LogIn, Camera, X } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/* IndianAirports — structured airport reviews.
   Anyone can read; posting requires a (free) TravelerPulse sign-in. */

const CATEGORIES = [
  ["cleanliness", "Cleanliness"],
  ["security", "Security & Queues"],
  ["food", "Food & Dining"],
  ["toilets", "Toilets"],
  ["parking", "Parking"],
  ["dropoff", "Drop-off / Pick-up"],
  ["wifi", "Wi-Fi & Charging"],
  ["staff", "Staff Helpfulness"],
];
const LABELS = Object.fromEntries(CATEGORIES);

function Stars({ value, size = "w-3.5 h-3.5" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "text-amber-500 fill-amber-400" : "text-zinc-300"}`} />
      ))}
    </span>
  );
}

function StarInput({ value, onChange, testid }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onClick={() => onChange(i === value ? 0 : i)} className="p-0.5" aria-label={`${i} stars`}>
          <Star className={`w-5 h-5 transition-colors ${i <= value ? "text-amber-500 fill-amber-400" : "text-zinc-300 hover:text-amber-400"}`} />
        </button>
      ))}
    </span>
  );
}

export default function IAReviews({ airport }) {
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState({});
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoNote, setPhotoNote] = useState("");

  const load = () => api.get(`/india/airports/${airport.iata}/reviews`).then((r) => {
    setData(r.data);
    if (r.data.my_review) {
      setRatings(r.data.my_review.ratings || {});
      setText(r.data.my_review.text || "");
    }
  }).catch(() => setData({ summary: { count: 0, categories: {} }, reviews: [] }));

  useEffect(() => { load(); }, [airport.iata, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const clean = Object.fromEntries(Object.entries(ratings).filter(([, v]) => v >= 1));
    if (Object.keys(clean).length === 0) return;
    setSaving(true);
    setError("");
    setPhotoNote("");
    try {
      // Optional photo rides the existing community pipeline (AI moderation + EXIF scrub)
      let photo_id;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        fd.append("entity_type", "india-airport");
        fd.append("entity_id", airport.iata);
        const up = await api.post("/community/photos", fd);
        photo_id = up.data?.photo?.id;
        if (up.data?.status === "rejected") {
          setError(`Photo not accepted: ${up.data?.reason || "moderation"} — review saved without it.`);
          photo_id = undefined;
        } else if (up.data?.status === "pending") {
          setPhotoNote("Photo is awaiting moderation and will appear once approved.");
        }
      }
      await api.post(`/india/airports/${airport.iata}/reviews`, { ratings: clean, text, ...(photo_id ? { photo_id } : {}) });
      setSaved(true);
      setPhotoFile(null);
      setTimeout(() => setSaved(false), 2500);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save your review — please try again.");
    } finally { setSaving(false); }
  };

  const s = data?.summary;
  const rated = Object.values(ratings).filter((v) => v >= 1).length;

  return (
    <div data-testid="ia-reviews">
      <h2 className="font-heading font-800 text-lg md:text-lg flex items-center gap-2">
        <Star className="w-5 h-5 text-blue-700" /> Traveler ratings — {airport.iata}
      </h2>

      {/* Summary */}
      {s && s.count > 0 ? (
        <div className="mt-3 bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5 flex flex-wrap gap-6" data-testid="ia-reviews-summary">
          <div className="text-center shrink-0">
            <div className="font-heading font-900 text-4xl text-zinc-900" data-testid="ia-reviews-overall">{s.overall}</div>
            <Stars value={s.overall} />
            <div className="text-[11px] text-zinc-500 mt-0.5">{s.count} review{s.count > 1 ? "s" : ""}</div>
          </div>
          <div className="flex-1 min-w-[240px] grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {CATEGORIES.map(([k, label]) => {
              const c = s.categories[k];
              return (
                <div key={k} className="flex items-center gap-2 text-xs">
                  <span className="w-32 shrink-0 text-zinc-600">{label}</span>
                  <span className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                    <span className="block h-full bg-amber-400 rounded-full" style={{ width: c?.avg ? `${(c.avg / 5) * 100}%` : 0 }} />
                  </span>
                  <span className="w-7 text-right font-700 text-zinc-700">{c?.avg ?? "—"}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-zinc-500" data-testid="ia-reviews-empty">No traveler ratings yet — be the first to rate {airport.city}'s airport.</p>
      )}

      {/* Write / edit */}
      {user ? (
        <div className="mt-4 bg-blue-50/60 border border-blue-200 rounded-2xl p-4 sm:p-5" data-testid="ia-review-form">
          <div className="text-sm font-800">{data?.my_review ? "Update your rating" : "Rate this airport"}</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2">
            {CATEGORIES.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-zinc-600">{label}</span>
                <StarInput value={ratings[k] || 0} onChange={(v) => setRatings((r) => ({ ...r, [k]: v }))} testid={`ia-rate-${k}`} />
              </div>
            ))}
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000}
            placeholder="Anything other travelers should know? (optional)" data-testid="ia-review-text"
            className="mt-3 w-full text-sm border border-zinc-300 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-blue-400" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs font-700 text-zinc-600 border border-zinc-300 bg-white rounded-full px-3 py-1.5 cursor-pointer hover:border-blue-400 hover:text-blue-700" data-testid="ia-review-photo-label">
              <Camera className="w-3.5 h-3.5" /> {photoFile ? "Change photo" : "Add a photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="ia-review-photo-input"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
            </label>
            {photoFile && (
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500" data-testid="ia-review-photo-preview">
                <img src={URL.createObjectURL(photoFile)} alt="" className="w-8 h-8 rounded object-cover border border-zinc-200" />
                <span className="max-w-[140px] truncate">{photoFile.name}</span>
                <button onClick={() => setPhotoFile(null)} aria-label="Remove photo" data-testid="ia-review-photo-remove"
                  className="text-zinc-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
              </span>
            )}
          </div>
          {photoNote && <p className="mt-1.5 text-[11px] text-amber-700" data-testid="ia-review-photo-note">{photoNote}</p>}
          <div className="mt-2 flex items-center gap-3">
            <button onClick={submit} disabled={saving || rated === 0} data-testid="ia-review-submit"
              className="bg-blue-700 text-white text-sm font-800 rounded-full px-5 py-2 hover:bg-blue-800 disabled:opacity-40">
              {saving ? "Saving…" : data?.my_review ? "Update review" : "Submit review"}
            </button>
            {rated === 0 && <span className="text-[11px] text-zinc-500">Tap stars on at least one category</span>}
            {saved && <span className="text-xs font-700 text-emerald-600" data-testid="ia-review-saved">Saved — thank you!</span>}
            {error && <span className="text-xs font-700 text-red-600" data-testid="ia-review-error">{error}</span>}
          </div>
        </div>
      ) : (
        <div className="mt-4 bg-white border border-dashed border-blue-300 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="ia-review-signin">
          <LogIn className="w-4 h-4 text-blue-700 shrink-0" />
          <span className="text-sm text-zinc-600 flex-1 min-w-[200px]">Flown through {airport.iata}? Sign in free with TravelerPulse to rate cleanliness, security, food and more.</span>
          <Link to={`/login?next=${encodeURIComponent(location.pathname)}&ref=indianairports`} data-testid="ia-review-signin-btn"
            className="bg-blue-700 text-white text-xs font-800 rounded-full px-4 py-2 hover:bg-blue-800">Sign in to review</Link>
        </div>
      )}

      {/* Recent reviews */}
      {data?.reviews?.length > 0 && (
        <div className="mt-4 space-y-2.5" data-testid="ia-reviews-list">
          {data.reviews.map((r) => (
            <div key={r.review_id} className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 font-800 text-zinc-800"><UserRound className="w-3.5 h-3.5 text-zinc-400" /> {r.user_name}</span>
                <Stars value={r.overall} />
                <span className="font-700 text-zinc-600">{r.overall}</span>
                <span className="text-zinc-400 ml-auto">{(r.updated_at || "").slice(0, 10)}</span>
              </div>
              {r.text && <p className="mt-1.5 text-sm text-zinc-600">{r.text}</p>}
              {r.photo_url && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${r.photo_url}`} alt={`Photo by ${r.user_name}`}
                  loading="lazy" data-testid="ia-review-photo"
                  className="mt-2 h-28 rounded-lg object-cover border border-zinc-200" />
              )}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {Object.entries(r.ratings || {}).map(([k, v]) => (
                  <span key={k} className="text-[10px] font-700 bg-zinc-100 text-zinc-600 rounded-full px-2 py-0.5">{LABELS[k]} {v}★</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
